import { useEffect, useMemo } from "react";
import { CONFIG_PAGES, HTTP_STATUSES } from "@miplatform/constants";
import { useProfilePicture, useRouter } from "@miplatform/hooks";
import { externalReferenceId, getRootUrl } from "@miplatform/utils";
import { useUser } from "@mibuildweb/hooks";
import { CONFIG_PATHS } from "../../../../routes/config/constants";
import { IUserFormData } from "../ConfigUsers.types";

export const useUserDetailsViewModel = ({
  reset,
}: {
  reset: (data?: Partial<IUserFormData> | undefined) => void;
}) => {
  const { navigate } = useRouter();

  const {
    getUser,
    createUser,
    updateUser,
    userDetails,
    loading: isUserLoading,
  } = useUser();

  const {
    pendingProfilePicture,
    setPendingProfilePicture,
    profilePicture,
    uploadPicture,
    removePicture,
    isLoading: isProfilePictureLoading,
  } = useProfilePicture({
    values: userDetails,
    getExternalReferenceId: () =>
      externalReferenceId.getProfilePhotoRefId({
        userId: userDetails?.id || "",
      }),
  });

  const handleCreateUser = async (data: IUserFormData) => {
    const userData = { ...data, userName: data.email };
    const response = await createUser(userData);
    if (response.status === HTTP_STATUSES.OK) {
      if (pendingProfilePicture) {
        await uploadPicture(
          pendingProfilePicture,
          externalReferenceId.getProfilePhotoRefId({ userId: response.data.id })
        );
      }
      navigate(CONFIG_PATHS[CONFIG_PAGES.USERS]);
    }
  };
  const handleUpdateUser = async (data: IUserFormData) => {
    if (pendingProfilePicture) {
      if (profilePicture?.id) {
        await removePicture();
      }
      await uploadPicture(
        pendingProfilePicture,
        externalReferenceId.getProfilePhotoRefId({
          userId: userDetails?.id || "",
        })
      );
    }
    const response = await updateUser(data, {
      onSuccess: () => {
        reset(data);
      },
    });
    if (response.status === HTTP_STATUSES.OK && data.id) {
      getUserDetails(data.id);
    }
  };

  const getUserDetails = async (id: string) => {
    const response = await getUser(id);

    if (response.status === HTTP_STATUSES.OK) {
      reset(response.data);
    }
  };

  const profilePictureSrc = useMemo(() => {
    if (pendingProfilePicture) {
      return URL.createObjectURL(pendingProfilePicture);
    }
    if (profilePicture) {
      return `${getRootUrl()}/${profilePicture.referenceId}`;
    }
    return undefined;
  }, [pendingProfilePicture, profilePicture]);

  useEffect(() => {
    return () => {
      if (pendingProfilePicture) {
        URL.revokeObjectURL(pendingProfilePicture);
      }
    };
  }, [pendingProfilePicture]);

  return {
    getUserDetails,
    handleCreateUser,
    handleUpdateUser,
    userDetails,
    profilePictureSrc,
    setPendingProfilePicture,
    pendingProfilePicture,
    isLoading: isUserLoading || isProfilePictureLoading,
  };
};
